import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import type {
  FinanceReadinessPreviewItem,
  FinanceReadinessPreviewTier,
} from "../../lib/dashboard";
import {
  DataSourceChooser,
  type DataSourceOption,
} from "../dashboard/overview/data-source-chooser";
import { ProfileCompletenessWidget } from "../dashboard/journey-widgets/profile-completeness-widget";
import { SectionLabel } from "../marketplace/OffersInbox";
import { selectNextMoveWidget } from "./NextMove.helpers";

export interface NextMoveTopOffer {
  proDisplayName?: string;
  expiresAt?: string | null;
}

export interface NextMoveProps {
  readinessOnly: boolean;
  isLive: boolean;
  verificationTier: FinanceReadinessPreviewTier;
  profileCompleteness: number;
  profileCompletenessMissing?: readonly string[];
  topOffer?: NextMoveTopOffer | null;
  onSelectVerificationSource: (
    source: DataSourceOption,
    item?: FinanceReadinessPreviewItem,
  ) => void;
  onCompleteOnboarding: () => void;
}

/**
 * Plan 435 M3 (mfh-next-move). Exactly ONE `JourneyRail` widget, chosen by
 * `selectNextMoveWidget` (tech-spec Components "NextMove"; design §4 row
 * 4). `PromptSuggestionsWidget` and `AddPropertyWidget` never render here —
 * they belong to the legacy Overview rail, not the open-market Home.
 */
export function NextMove({
  readinessOnly,
  isLive,
  verificationTier,
  profileCompleteness,
  profileCompletenessMissing,
  topOffer,
  onSelectVerificationSource,
  onCompleteOnboarding,
}: NextMoveProps) {
  const widget = selectNextMoveWidget({
    readinessOnly,
    isLive,
    verificationTier,
    topOfferExpiresAt: topOffer?.expiresAt,
  });

  return (
    // Label outside the card, matching every other Home section (Prepare,
    // Offers for you): the heading belongs to the page, the card to content.
    <section className="space-y-3" data-testid="home-next-move">
      <SectionLabel label="Your next move" />
      <div className="marketplace-surface p-4 sm:p-5">
        {widget === "offers-expiry" ? (
          <OffersExpiryWidget topOffer={topOffer ?? null} />
        ) : widget === "data-source" ? (
          <DataSourceChooser onSelect={onSelectVerificationSource} />
        ) : profileCompleteness >= 100 ? (
          // A finished profile has no "add more" step — a full bar labelled
          // "nothing left to add" isn't a move. Point at what still moves
          // readiness instead (2026-10-08).
          <ReadinessPointer />
        ) : (
          <ProfileCompletenessWidget
            missing={profileCompletenessMissing}
            onContinue={onCompleteOnboarding}
            percent={profileCompleteness}
          />
        )}
      </div>
    </section>
  );
}

function OffersExpiryWidget({
  topOffer,
}: {
  topOffer: NextMoveTopOffer | null;
}) {
  const name = topOffer?.proDisplayName ?? "an agent";
  return (
    <div
      className="flex w-full flex-col gap-2"
      data-testid="home-next-move-offers-expiry"
    >
      <p className="text-sm font-medium text-foreground">
        Your top offer from {name} is expiring soon
      </p>
      <p className="text-xs text-muted-foreground">
        Compare it against the rest before it closes.
      </p>
      <Link
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-primary"
        to="/offers"
      >
        View offers
        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function ReadinessPointer() {
  return (
    <div
      className="flex w-full flex-col gap-2"
      data-testid="home-next-move-readiness"
    >
      <p className="text-sm font-medium text-foreground">
        Your profile is complete
      </p>
      <p className="text-xs text-muted-foreground">
        See what still raises your readiness and what Ready to act needs.
      </p>
      <Link
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-primary"
        to="/dashboard?view=readiness"
      >
        See your path to ready
        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
